import { useEffect } from "react";
import Box from "@mui/material/Box";
import BoardCell from "./BoardCell";

const Board = ({ state, updateState }) => {
  const GridCell = (row, col, isMine) => {
    return {
      col: col,
      row: row,
      neighbor: 0,
      isMine: isMine,
      isRevealed: false,
      isFlagged: false,
      isUnknown: false,
      isClicked: false,
      get isEmpty() {
        return this.neighbor === 0 && this.isMine === false;
      },
    };
  };

  const createNewBoard = (click = null) => {
    const grid = [];
    const rows = state.width;
    const columns = state.height;
    const minesCount = state.mines;
    const minesArray = getRandomMines(minesCount, columns, rows, click);

    for (let row = 0; row < columns; ++row) {
      grid.push([]);
      for (let col = 0; col < rows; ++col) {
        const gridCell = GridCell(
          row,
          col,
          minesArray.includes(row * rows + col)
        );
        addGridCell(grid, gridCell);
      }
    }
    return grid;
  };

  const getRandomMines = (amount, columns, rows, starter = null) => {
    const minesArray = [];
    const limit = columns * rows;
    const minesPool = [...Array(limit).keys()];

    if (starter > 0 && starter < limit) {
      minesPool.splice(starter, 1);
    }

    for (let i = 0; i < amount; ++i) {
      const n = Math.floor(Math.random() * minesPool.length);
      minesArray.push(...minesPool.splice(n, 1));
    }
    return minesArray;
  };

  const addGridCell = (grid, gridCell) => {
    const row = grid.length - 1;
    const col = grid[row].length;
    const lastGridCell = gridCell;
    const neighbors = getNeighbors(grid, row, col);

    for (let neighborGridCell of neighbors) {
      if (lastGridCell.isMine) {
        neighborGridCell.neighbor += 1;
      } else if (neighborGridCell.isMine) {
        lastGridCell.neighbor += 1;
      }
    }

    grid[row].push(lastGridCell);
  };

  const getNeighbors = (grid, row, col) => {
    const neighbors = [];
    const currentRow = grid[row];
    const prevRow = grid[row - 1];
    const nextRow = grid[row + 1];

    if (currentRow[col - 1]) neighbors.push(currentRow[col - 1]);
    if (currentRow[col + 1]) neighbors.push(currentRow[col + 1]);
    if (prevRow) {
      if (prevRow[col - 1]) neighbors.push(prevRow[col - 1]);
      if (prevRow[col]) neighbors.push(prevRow[col]);
      if (prevRow[col + 1]) neighbors.push(prevRow[col + 1]);
    }
    if (nextRow) {
      if (nextRow[col - 1]) neighbors.push(nextRow[col - 1]);
      if (nextRow[col]) neighbors.push(nextRow[col]);
      if (nextRow[col + 1]) neighbors.push(nextRow[col + 1]);
    }
    return neighbors;
  };

  const revealBoard = () => {
    const grid = state.grid;

    for (const row of grid) {
      for (const gridCell of row) {
        if (gridCell.isMine) {
          gridCell.isRevealed = true;
        }
      }
    }
  };

  const revealEmptyNeighbors = (grid, row, col) => {
    const neighbors = [...getNeighbors(grid, row, col)];
    grid[row][col].isFlagged = false;
    grid[row][col].isRevealed = true;

    while (neighbors.length) {
      const neighborGridCell = neighbors.shift();
      if (neighborGridCell.isRevealed) {
        continue;
      }
      if (neighborGridCell.isEmpty) {
        neighbors.push(
          ...getNeighbors(grid, neighborGridCell.row, neighborGridCell.col)
        );
      }
      neighborGridCell.isFlagegd = false;
      neighborGridCell.isRevealed = true;
    }
  };

  const getFlaggedMineNeighbors = (grid, row, col) => {
    var neighborCount = 0;
    const neighbors = [...getNeighbors(grid, row, col)];
    while (neighbors.length) {
      const neighborGridCell = neighbors.shift();
      if (neighborGridCell.isFlagged && neighborGridCell.isMine) {
        neighborCount++;
      }
    }
    return neighborCount;
  };

  const killBoard = (type) => {
    const message = type === "lost" ? 20 : 10;

    revealBoard();
    updateState({ ...state, gameStatus: message, timeRunning: false });
  };

  const checkVictory = () => {
    const revealed = getRevealed();
    const flaggedRight = getFlagged();
    if (revealed >= state.height * state.width - state.mines || flaggedRight === state.mines) {
      killBoard("win");
    }
  };

  const getRevealed = () => {
    return state.grid
      .reduce((r, v) => {
        r.push(...v);
        return r;
      }, [])
      .map((v) => v.isRevealed)
      .filter((v) => !!v).length;
  };

  const getFlagged = () => {
    return state.grid
      .reduce((r, v) => {
        r.push(...v);
        return r;
      }, [])
      .map((v) => v.isFlagged && v.isMine)
      .filter((v) => !!v).length;
  };

  const handleLeftClick = (row, col) => {
    if (state.gameStatus < 20) {
      const grid = state.grid;
      const gridCell = grid[row][col];

      gridCell.isClicked = true;

      if (
        gridCell.isRevealed &&
        gridCell.neighbor === getFlaggedMineNeighbors(grid, row, col)
      ) {
        revealEmptyNeighbors(grid, row, col);
      }

      // Might want to add an "isUnknown" state later
      if (gridCell.isRevealed || gridCell.isFlagged) {
        return false;
      }

      // End game if mine
      if (gridCell.isMine) {
        killBoard("lost");
        return false;
      }

      if (gridCell.isEmpty) {
        revealEmptyNeighbors(grid, row, col);
      }

      gridCell.isFlagged = false;
      gridCell.isRevealed = true;

      updateState({
        ...state,
        grid: grid,
        gameStatus: 1,
        timeRunning: true,
        clicks: state.clicks + 1,
      });

      checkVictory();
    }
  };

  const handleRightClick = (e, row, col) => {
    if (state.gameStatus < 20) {
      e.preventDefault();
      const grid = state.grid;
      let minesLeft = state.leftMines;

      // Check if already revealed
      if (grid[row][col].isRevealed) return false;

      if (grid[row][col].isFlagged) {
        grid[row][col].isFlagged = false;
        minesLeft++;
      } else {
        if (minesLeft > 0) {
          grid[row][col].isFlagged = true;
          minesLeft--;
        }
      }
      updateState({
        ...state,
        leftMines: minesLeft,
        gameStatus: 1,
        timeRunning: true,
        clicks: state.clicks + 1,
      });

      checkVictory();
    }
  };

  const RenderBoard = () => {
    const grid = state.grid;
    return grid.map((row, index) => {
      const rowCells = row.map((gridCell) => {
        return (
          <BoardCell
            key={gridCell.row * row.length + gridCell.col}
            onClick={(row, col) => handleLeftClick(row, col)}
            cMenu={(e, row, col) => handleRightClick(e, row, col)}
            value={gridCell}
          />
        );
      });
      return (
        <Box key={index} sx={{ display: "flex", height: "1.5rem" }}>
          {rowCells}
        </Box>
      );
    });
  };

  useEffect(() => {
    if (state.gameStatus === 0) {
      const grid = createNewBoard();
      updateState({ ...state, grid: grid });
    }
    //eslint-disable-next-line
  }, [state.gameStatus]);

  useEffect(() => {
    updateState({ ...state, leftMines: state.mines, grid: createNewBoard() });
    //eslint-disable-next-line
  }, [state.mines]);

  useEffect(() => {
    if (state.grid.length === 0) {
      updateState({ ...state, grid: createNewBoard() });
    }
  });

  return (
    <Box>
      <RenderBoard />
    </Box>
  );
};

export default Board;
